import type { BrowserRouter } from '../../app/router';
import { loadSciChapter, loadSciManifest, SciDataError } from '../../data/sci';
import type {
  SciChapterShard,
  SciCitation,
  SciCorpus,
  SciManifest,
  ScriptureSelection,
} from '../../data/schema';
import { scriptureRoute } from './sciLinks';

interface VerseHistoryOptions {
  selection: ScriptureSelection;
  router: BrowserRouter;
  showFullRouteLink?: boolean;
}

export const CORPUS_LABELS: Record<SciCorpus, string> = {
  'general-conference': 'General Conference',
  'journal-of-discourses': 'Journal of Discourses',
  tpjs: 'Teachings of the Prophet Joseph Smith (TPJS)',
};

const CORPUS_ORDER: SciCorpus[] = [
  'general-conference',
  'journal-of-discourses',
  'tpjs',
];

interface CitationOccurrence {
  citation: SciCitation;
  displayRange: string;
}

function externalLink(href: string, label: string): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = href;
  link.target = '_blank';
  link.rel = 'noreferrer';
  link.textContent = label;
  return link;
}

export function rangesForSelection(
  shard: SciChapterShard,
  verses: number[],
): SciChapterShard['ranges'] {
  const selected = new Set(verses);
  return shard.ranges.filter((range) =>
    range.exactVerses.some((verse) => selected.has(verse)),
  );
}

export function citationOccurrences(
  shard: SciChapterShard,
  verses: number[],
): CitationOccurrence[] {
  return rangesForSelection(shard, verses).flatMap((range) =>
    range.citations.map((citation) => ({
      citation,
      displayRange: range.displayRange,
    })),
  );
}

function decadeFor(citation: SciCitation): string {
  if (citation.year === null) return 'Undated';
  return `${String(Math.floor(citation.year / 10) * 10)}s`;
}

function citationCategory(citation: SciCitation): string {
  if (citation.corpus === 'general-conference' && citation.year === 2026) {
    return 'April 2026 General Conference';
  }
  return CORPUS_LABELS[citation.corpus];
}

function categoryPriority(category: string): number {
  if (category === 'April 2026 General Conference') return 0;
  const corpusIndex = CORPUS_ORDER.findIndex(
    (corpus) => CORPUS_LABELS[corpus] === category,
  );
  return corpusIndex < 0 ? 99 : corpusIndex + 1;
}

function talkKey(occurrence: CitationOccurrence): string {
  return `${occurrence.citation.corpus}:${occurrence.citation.talkId}`;
}

function renderTalkGroup(occurrences: CitationOccurrence[]): HTMLElement {
  const first = occurrences[0].citation;
  const card = document.createElement('article');
  card.className = 'citation-talk';
  card.dataset.talkId = first.talkId;
  const title = document.createElement('h4');
  title.textContent =
    first.title ||
    (first.corpus === 'tpjs'
      ? 'Teachings of the Prophet Joseph Smith'
      : 'Untitled source');
  const meta = document.createElement('p');
  meta.className = 'citation-talk__meta';
  meta.textContent = [
    first.speaker,
    first.year === null ? null : String(first.year),
    first.session === 'A'
      ? 'April'
      : first.session === 'O'
        ? 'October'
        : null,
  ]
    .filter((value): value is string => Boolean(value))
    .join(' · ');
  const contextLabel = document.createElement('p');
  contextLabel.className = 'citation-talk__count';
  contextLabel.textContent = `${String(occurrences.length)} citation ${
    occurrences.length === 1 ? 'context' : 'contexts'
  }`;
  const contexts = document.createElement('ol');
  contexts.className = 'citation-contexts';
  for (const occurrence of occurrences) {
    const item = document.createElement('li');
    item.dataset.contextId = occurrence.citation.contextId;
    const reference = document.createElement('span');
    reference.textContent = occurrence.citation.rawReference;
    const details = document.createElement('span');
    details.className = 'citation-context__details';
    details.textContent =
      `SCI range ${occurrence.displayRange}, page ${occurrence.citation.page}, ` +
      `context ${occurrence.citation.contextId}`;
    item.append(reference, details);
    contexts.append(item);
  }
  const links = document.createElement('div');
  links.className = 'citation-talk__links';
  if (first.watchUrl) links.append(externalLink(first.watchUrl, 'Watch'));
  if (first.listenUrl) links.append(externalLink(first.listenUrl, 'Listen'));
  card.append(title, meta, contextLabel, contexts);
  if (links.childElementCount > 0) card.append(links);
  return card;
}

function renderResults(
  root: HTMLElement,
  occurrences: CitationOccurrence[],
): void {
  root.replaceChildren();
  const categoryGroups = new Map<string, CitationOccurrence[]>();
  for (const occurrence of occurrences) {
    const category = citationCategory(occurrence.citation);
    const group = categoryGroups.get(category) ?? [];
    group.push(occurrence);
    categoryGroups.set(category, group);
  }
  const categories = [...categoryGroups].sort(
    ([first], [second]) => categoryPriority(first) - categoryPriority(second),
  );
  for (const [category, categoryOccurrences] of categories) {
    const categorySection = document.createElement('section');
    categorySection.className = 'citation-corpus';
    categorySection.dataset.corpus = category;
    const heading = document.createElement('h2');
    heading.textContent = category;
    const decadeGroups = new Map<string, CitationOccurrence[]>();
    for (const occurrence of categoryOccurrences) {
      const decade = decadeFor(occurrence.citation);
      const group = decadeGroups.get(decade) ?? [];
      group.push(occurrence);
      decadeGroups.set(decade, group);
    }
    const decades = [...decadeGroups].sort(([first], [second]) => {
      if (first === 'Undated') return 1;
      if (second === 'Undated') return -1;
      return second.localeCompare(first);
    });
    categorySection.append(heading);
    for (const [decade, decadeOccurrences] of decades) {
      const decadeSection = document.createElement('section');
      decadeSection.className = 'citation-decade';
      decadeSection.dataset.decade = decade;
      const decadeHeading = document.createElement('h3');
      decadeHeading.textContent =
        category === 'April 2026 General Conference' ? 'Current conference' : decade;
      const talks = new Map<string, CitationOccurrence[]>();
      for (const occurrence of decadeOccurrences) {
        const key = talkKey(occurrence);
        const group = talks.get(key) ?? [];
        group.push(occurrence);
        talks.set(key, group);
      }
      decadeSection.append(decadeHeading);
      for (const talkOccurrences of talks.values()) {
        decadeSection.append(renderTalkGroup(talkOccurrences));
      }
      categorySection.append(decadeSection);
    }
    root.append(categorySection);
  }
}

function renderLoaded(
  root: HTMLElement,
  selection: ScriptureSelection,
  manifest: SciManifest,
  shard: SciChapterShard,
  router: BrowserRouter,
  showFullRouteLink: boolean,
): void {
  root.dataset.state = 'ready';
  const matchingRanges = rangesForSelection(shard, selection.verses);
  const occurrences = citationOccurrences(shard, selection.verses);
  const heading = document.createElement('div');
  heading.className = 'verse-history__summary';
  const total = document.createElement('p');
  total.className = 'verse-history__total';
  const distinctTalks = new Set(occurrences.map(talkKey)).size;
  total.textContent =
    `${String(occurrences.length)} available citation occurrences across ` +
    `${String(distinctTalks)} sources`;
  const rangeCopy = document.createElement('p');
  rangeCopy.className = 'muted';
  rangeCopy.textContent = `${String(matchingRanges.length)} exact SCI ranges contain the selected verse set.`;
  heading.append(total, rangeCopy);

  const outbound = document.createElement('div');
  outbound.className = 'verse-history__outbound';
  outbound.append(
    externalLink(selection.sciUrl, 'Open in Scripture Citation Index'),
    externalLink(selection.officialChurchUrl, 'Read in Gospel Library'),
  );
  if (showFullRouteLink) {
    const full = document.createElement('a');
    full.href = scriptureRoute(
      selection.bookId,
      selection.chapter,
      selection.verses,
    );
    full.dataset.router = '';
    full.textContent = 'Open full verse history';
    full.addEventListener('click', (event) => {
      event.preventDefault();
      router.navigate(full.href);
    });
    outbound.prepend(full);
  }

  const gapCount = shard.knownGapCount;
  const gap = document.createElement('div');
  gap.className = 'source-callout source-callout--warning';
  gap.setAttribute('role', 'note');
  if (gapCount > 0) {
    gap.textContent =
      `${String(gapCount)} badge-count citations in this chapter do not have ` +
      'materialized citation rows. Counts here describe available rows only.';
  }

  const filters = document.createElement('form');
  filters.className = 'verse-filters';
  const corpusFieldset = document.createElement('fieldset');
  const legend = document.createElement('legend');
  legend.textContent = 'Corpus';
  corpusFieldset.append(legend);
  const corpusInputs = new Map<SciCorpus, HTMLInputElement>();
  for (const corpus of CORPUS_ORDER) {
    const count = occurrences.filter(
      (occurrence) => occurrence.citation.corpus === corpus,
    ).length;
    const label = document.createElement('label');
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = true;
    input.value = corpus;
    corpusInputs.set(corpus, input);
    label.append(
      input,
      document.createTextNode(
        ` ${CORPUS_LABELS[corpus]} (${String(count)})`,
      ),
    );
    corpusFieldset.append(label);
  }
  const decadeLabel = document.createElement('label');
  decadeLabel.textContent = 'Decade ';
  const decadeSelect = document.createElement('select');
  decadeSelect.setAttribute('aria-label', 'Decade');
  const allDecades = document.createElement('option');
  allDecades.value = 'all';
  allDecades.textContent = 'All decades';
  decadeSelect.append(allDecades);
  const decades = [
    ...new Set(occurrences.map((occurrence) => decadeFor(occurrence.citation))),
  ].sort((first, second) => {
    if (first === 'Undated') return 1;
    if (second === 'Undated') return -1;
    return second.localeCompare(first);
  });
  for (const decade of decades) {
    const option = document.createElement('option');
    option.value = decade;
    option.textContent = decade;
    decadeSelect.append(option);
  }
  decadeLabel.append(decadeSelect);
  filters.append(corpusFieldset, decadeLabel);

  const filteredStatus = document.createElement('p');
  filteredStatus.className = 'inline-status verse-history__filtered-total';
  filteredStatus.setAttribute('role', 'status');
  filteredStatus.setAttribute('aria-live', 'polite');
  const results = document.createElement('div');
  results.className = 'verse-results';
  const update = (): void => {
    const enabled = new Set(
      [...corpusInputs]
        .filter(([, input]) => input.checked)
        .map(([corpus]) => corpus),
    );
    const filtered = occurrences.filter(
      (occurrence) =>
        enabled.has(occurrence.citation.corpus) &&
        (decadeSelect.value === 'all' ||
          decadeFor(occurrence.citation) === decadeSelect.value),
    );
    filteredStatus.textContent =
      `${String(filtered.length)} available occurrences in ` +
      `${String(new Set(filtered.map(talkKey)).size)} sources after filters.`;
    renderResults(results, filtered);
    if (filtered.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'state-panel';
      empty.textContent = 'No available citation rows match these filters.';
      results.append(empty);
    }
  };
  for (const input of corpusInputs.values()) {
    input.addEventListener('change', update);
  }
  decadeSelect.addEventListener('change', update);

  const attribution = document.createElement('p');
  attribution.className = 'sci-attribution';
  attribution.textContent =
    `Citation data: Scripture Citation Index at scriptures.byu.edu, ` +
    `the Galbraith and Liddle project. Dataset ${manifest.datasetVersion}, ` +
    `${String(manifest.counts.materializedCitations)} available contexts.`;

  root.replaceChildren(heading, outbound);
  if (gapCount > 0) root.append(gap);
  if (occurrences.length === 0) {
    root.dataset.state = 'missing-range';
    const missing = document.createElement('div');
    missing.className = 'state-panel';
    missing.setAttribute('role', 'status');
    missing.textContent =
      'No materialized SCI range contains this exact verse selection.';
    root.append(missing, attribution);
    return;
  }
  root.append(filters, filteredStatus, results, attribution);
  update();
}

function renderLoadError(root: HTMLElement, error: unknown): void {
  const state = document.createElement('div');
  state.className = 'state-panel state-panel--error';
  state.setAttribute('role', 'alert');
  if (!navigator.onLine) {
    root.dataset.state = 'offline-not-cached';
    state.textContent =
      'This SCI chapter is not cached. Reconnect to load its citation history.';
  } else if (error instanceof SciDataError && error.status === 404) {
    root.dataset.state = 'missing-chapter';
    state.textContent = 'No packaged SCI chapter exists for this reference.';
  } else {
    root.dataset.state = 'load-error';
    state.textContent =
      'Citation history could not be loaded. Refresh this page to try again.';
  }
  root.replaceChildren(state);
}

export function createVerseHistory(options: VerseHistoryOptions): HTMLElement {
  const root = document.createElement('div');
  root.className = 'verse-history';
  root.dataset.state = 'loading';
  const loading = document.createElement('div');
  loading.className = 'state-panel';
  loading.setAttribute('role', 'status');
  loading.textContent = 'Loading local citation history…';
  root.append(loading);
  void Promise.all([
    loadSciManifest(),
    loadSciChapter(options.selection.bookId, options.selection.chapter),
  ])
    .then(([manifest, shard]) => {
      renderLoaded(
        root,
        options.selection,
        manifest,
        shard,
        options.router,
        options.showFullRouteLink ?? false,
      );
    })
    .catch((error: unknown) => renderLoadError(root, error));
  return root;
}
